<template>
  <!--  show PWA-->
  <!--  <PWABlock v-if="true" />-->
  <SomethingGoingWrong
    v-if="store.state.app.isCrashed"
  />
  <!--
    QR install gate is for top-level browser opens only.
    Landing embeds (/desktop/auth, /payment-form*) run in a third-party iframe with
    its own SW/storage partition — never show PWA QR there.
  -->
  <PWABlock
    v-else-if="!state.isStandalone && !state.isEmbedded && !route.meta.isPublic && !route.meta.isDesktop"
  />
  <NewVersionAvailable
    v-else-if="!store.state.user.isRestoring && (store.state.app.isNewVersionAvailable || store.state.app.isVersionRefreshing)"
  />
  <NoConnection
    v-else-if="!store.state.app.isOnline"
  />
  <router-view
    v-else-if="route.meta.isDesktop && !store.state.user.isRestoring"
  />
  <router-view
    :key="mobileViewKey"
    v-else-if="!store.state.user.isRestoring"
  />
  <transition
    v-show="store.state.user.isRestoring"
    name="fade"
  >
    <SplashScreen
      v-if="state.splashScreenNeeded"
    />
  </transition>

  <component
    :is="messageComponents[store.state.app.messageComponent]"
    v-if="store.state.app.messageComponent"
    :data="store.state.app.messageComponentData"
  />

  <ViewportDebugOverlay />
</template>

<script setup>
import {computed, defineAsyncComponent, onBeforeMount, onUnmounted, reactive, watch} from 'vue'
import {useRoute} from 'vue-router'
import store from './store/store'
import SplashScreen from '@/views/SplashScreen.vue'
import {imagesLinkCache} from '@/utils/imagesLinkCache'
import NoConnection from '@/views/system/NoConnection.vue'
import SomethingGoingWrong from '@/views/system/SomethingGoingWrong.vue'
import {pwaUtils} from "@/utils/pwaUtils.js"
import NewVersionAvailable from '@/views/system/NewVersionAvailable.vue'
import {yandexMetrika} from "@/external/yandexWebisor.js"
import {messageComponents} from '@/components/messageComponents/messageComponents.js'
import {appType, appTypes} from '@/appType/appType.js'
import ViewportDebugOverlay from '@/components/debug/ViewportDebugOverlay.vue'
import {startDocumentHeightSync} from '@/utils/syncDocumentHeight.js'
import {startTelegramViewportHeightSync} from '@/utils/syncTelegramViewportHeight.js'

const PWABlock = defineAsyncComponent(() => import('@/views/system/PWABlock.vue'))

const route = useRoute()

/** Events + date filter share one page instance — fullPath key would remount calendar on date change */
const mobileViewKey = computed(() => {
  if (route.name === 'Events' || route.name === 'EventsByDate') {
    return 'events'
  }

  return route.fullPath
})

const state = reactive({
  splashScreenNeeded: true,
  isStandalone: pwaUtils.isStandalone(),
  // iframe on landing (younesis.com/pay#popup:*) — separate storage from first-party tab
  isEmbedded: typeof window !== 'undefined' && window.parent !== window,
  checkInterval: null,
  scrollTop: document.documentElement.scrollTop,
})

let stopDocumentHeightSync = null
let stopTelegramViewportSync = null

watch(() => store.state.user.isRestoring, isRestoring => {
  if (!isRestoring) {
    // Theme CSS vars (button colors etc.) are applied in app/init during restore.
    // Keep the index.html boot-loader until then to avoid unstyled NewVersionAvailable FOUC.
    const boot = document.getElementById('boot-loader')
    if (boot) {
      boot.hidden = true
      boot.remove()
    }

    setTimeout(() => {
      state.splashScreenNeeded = false
    }, 500)
  } else {
    state.splashScreenNeeded = true
  }
}, { immediate: true })

onBeforeMount(async () => {
  imagesLinkCache.restore()
  document.body.classList.add('dark-theme')

  if (state.isStandalone) {
    yandexMetrika('reachGoal', 'appInstalled')
  }

  // PWA only: restore height after external payment return (stuck small svh).
  // Never on Telegram Mini App — isStandalone() is true for TMA, but screen.height
  // is the monitor while innerHeight is the iframe; collapse heuristic then sets
  // --document-height ≈ screen (e.g. 1393px in a 590px iframe) and clips scroll/nav.
  if (state.isStandalone && appType !== appTypes.TELEGRAM) {
    stopDocumentHeightSync = startDocumentHeightSync()
  } else if (appType === appTypes.TELEGRAM) {
    const root = document.documentElement
    root.classList.add('app-telegram')
    root.style.removeProperty('--document-height')
    root.style.removeProperty('--document-offset-top')
    root.style.removeProperty('--document-offset-bottom')
    // Viewport CSS vars for bar pin; shell height is not locked (html.app-telegram).
    stopTelegramViewportSync = startTelegramViewportHeightSync()
  }
})

onUnmounted(() => {
  clearInterval(state.checkInterval)
  stopDocumentHeightSync?.()
  stopTelegramViewportSync?.()
})

</script>

<style lang="scss">
@import "@/assets/variables";

:root {
  // Fallback until startTelegramViewportHeightSync sets a px value (bar / consumers).
  --document-height: calc(100svh - 0px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease, background-color .3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  background-color: transparent;
}
</style>
